import 'app/config/dayjs';
import 'react-toastify/dist/ReactToastify.css';
import './app.scss';

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';

import { Box } from '@mui/material';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import AppRoutes from 'app/routes';
import { hasAnyAuthority, isUserAuthorizedForMaintenanceByCompany } from 'app/shared/auth/private-route';
import ErrorBoundary from 'app/shared/error/error-boundary';
import { getProfile } from 'app/shared/reducers/application-profile';
import { getSession } from 'app/shared/reducers/authentication';
import { ChecklistFacilDrawer } from './checklist-facil/components/ChecklistFacilDrawer';
import { ChecklistFacilHeader } from './checklist-facil/components/ChecklistFacilHeader';
import { CheckListRoutes } from './checklist-facil/routes';
import { AppThemeProvider } from './shared/contexts/ThemeContext';
import { DefaultDrawer } from './shared/layout/drawer/DefaultDrawer';
import { DefaultHeader } from './shared/layout/header/DefaultHeader';
// import { BetaVersionProvider } from './modules/dashboards/shared/BetaVersionContext';

const baseHref = document.querySelector('base').getAttribute('href').replace(/\/$/, '');

export const App = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(getSession());
    dispatch(getProfile());
  }, []);

  const currentLocale = useAppSelector(state => state.locale.currentLocale);
  const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isManager = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANAGER]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isManufacturerManager = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_MANAGER]));
  const isManufacturerTechnician = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_TECHNICIAN]));
  const isManufacturerUser = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_USER]));
  const isTechnician = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TECHNICIAN]));
  const isDriver = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.DRIVER]));
  const isMaintenance = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MAINTENANCE]));
  const isChecklist = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.CHECKLIST]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isTransporterDriver = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_DRIVER]));
  const isTransporterManager = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_MANAGER]));
  const istransporterUser = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_USER]));
  const isRepresentativeAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.REPRESENTATIVE_ADMIN]));
  const ribbonEnv = useAppSelector(state => state.applicationProfile.ribbonEnv);
  const isInProduction = useAppSelector(state => state.applicationProfile.inProduction);
  const isOpenAPIEnabled = useAppSelector(state => state.applicationProfile.isOpenAPIEnabled);
  const account = useAppSelector(state => state.authentication.account);
  const isMaintenanceByCompany = useAppSelector(state =>
    isUserAuthorizedForMaintenanceByCompany(state.authentication.account.authorities, state.authentication.account.company));
  
  return (
    <Router basename={baseHref}>
      <AppThemeProvider>
        {/* <BetaVersionProvider> */}
        <Box>
          <ToastContainer position={toast.POSITION.TOP_LEFT} className="toastify-container" toastClassName="toastify-toast" />
          {account && account.login ? (
            <>
              <ErrorBoundary>
                {isChecklist ? (
                  <ChecklistFacilHeader ribbonEnv={ribbonEnv} setOpen={setOpen} isInProduction={isInProduction} open={open} />
                ) : (
                  <DefaultHeader
                    ribbonEnv={ribbonEnv}
                    isInProduction={isInProduction}
                    open={open}
                    setOpen={setOpen}
                    isDriver={isDriver}
                    isAdmin={isAdmin}
                    isMaintenanceByCompany={isMaintenanceByCompany}
                  />
                )}
              </ErrorBoundary>
              <ErrorBoundary>
                {isChecklist ? (
                  <ChecklistFacilDrawer isAuthenticated={isAuthenticated} setOpen={setOpen} open={open} />
                ) : (
                  <DefaultDrawer
                    isAuthenticated={isAuthenticated}
                    isAdmin={isAdmin}
                    isManager={isManager}
                    isManufacturerAdmin={isManufacturerAdmin}
                    isManufacturerManager={isManufacturerManager}
                    isManufacturerTechnician={isManufacturerTechnician}
                    isManufacturerUser={isManufacturerUser}
                    isTechnician={isTechnician}
                    isMaintenance={isMaintenance}
                    isMaintenanceByCompany={isMaintenanceByCompany}
                    isDriver={isDriver}
                    isTransporterAdmin={isTransporterAdmin}
                    isTransporterDriver={isTransporterDriver}
                    isTransporterManager={isTransporterManager}
                    istransporterUser={istransporterUser}
                    isRepresentativeAdmin={isRepresentativeAdmin}
                    currentLocale={currentLocale}
                    isOpenAPIEnabled={isOpenAPIEnabled}
                    setOpen={setOpen}
                    open={open}
                  />
                )}
              </ErrorBoundary>
            </>
          ) : (
            ''
          )}
          <ErrorBoundary>
            <ErrorBoundary>{isChecklist ? <CheckListRoutes /> : <AppRoutes isAuthenticated={isAuthenticated} />}</ErrorBoundary>
          </ErrorBoundary>
        </Box>
        {/* </BetaVersionProvider> */}
      </AppThemeProvider>
    </Router>
  );
};

export default App;
